import type { SpareCategoryTreeNode } from '../spare-categories/useData';
import type { SpareItem } from './useData';

export type CategoryTreeRow = SpareCategoryTreeNode & {
  depth: number;
  hasChildren: boolean;
};

export const flattenCategoryTree = (
  nodes: SpareCategoryTreeNode[],
  collapsedIds: Set<number>,
  depth = 0,
): CategoryTreeRow[] => {
  const rows: CategoryTreeRow[] = [];
  for (const node of nodes) {
    const hasChildren = (node.children?.length ?? 0) > 0;
    rows.push({ ...node, depth, hasChildren });
    if (hasChildren && !collapsedIds.has(node.id)) {
      rows.push(...flattenCategoryTree(node.children, collapsedIds, depth + 1));
    }
  }
  return rows;
};

const walkFilterTree = (
  nodes: SpareCategoryTreeNode[],
  selfMatch: (node: SpareCategoryTreeNode) => boolean,
): SpareCategoryTreeNode[] => {
  const walk = (node: SpareCategoryTreeNode): SpareCategoryTreeNode | null => {
    const filteredChildren = (node.children ?? [])
      .map(walk)
      .filter((child): child is SpareCategoryTreeNode => child != null);
    if (selfMatch(node) || filteredChildren.length > 0) {
      return { ...node, children: filteredChildren };
    }
    return null;
  };
  return nodes.map(walk).filter((node): node is SpareCategoryTreeNode => node != null);
};

export const filterCategoryTree = (nodes: SpareCategoryTreeNode[], term: string): SpareCategoryTreeNode[] => {
  const q = term.trim().toLowerCase();
  if (!q) return nodes;
  return walkFilterTree(
    nodes,
    (node) =>
      node.name.toLowerCase().includes(q) ||
      (node.abrv?.toLowerCase().includes(q) ?? false),
  );
};

export const findCategoryNodeById = (
  nodes: SpareCategoryTreeNode[],
  id: number,
): SpareCategoryTreeNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findCategoryNodeById(node.children ?? [], id);
    if (found) return found;
  }
  return null;
};

export const getCategoryPath = (
  nodes: SpareCategoryTreeNode[],
  id: number,
): SpareCategoryTreeNode[] => {
  const walk = (list: SpareCategoryTreeNode[], trail: SpareCategoryTreeNode[]): SpareCategoryTreeNode[] | null => {
    for (const node of list) {
      const next = [...trail, node];
      if (node.id === id) return next;
      const found = walk(node.children ?? [], next);
      if (found) return found;
    }
    return null;
  };
  return walk(nodes, []) ?? [];
};

export const collectExpandableCategoryIds = (nodes: SpareCategoryTreeNode[]): number[] => {
  const ids: number[] = [];
  const walk = (list: SpareCategoryTreeNode[]) => {
    for (const node of list) {
      if ((node.children?.length ?? 0) > 0) {
        ids.push(node.id);
        walk(node.children);
      }
    }
  };
  walk(nodes);
  return ids;
};

/** Collapse everything below roots (roots stay expanded). */
export const collectCollapsedIdsBelowRoot = (nodes: SpareCategoryTreeNode[]): number[] => {
  const ids: number[] = [];
  const walk = (list: SpareCategoryTreeNode[], isRoot: boolean) => {
    for (const node of list) {
      if (!isRoot && (node.children?.length ?? 0) > 0) ids.push(node.id);
      walk(node.children ?? [], false);
    }
  };
  walk(nodes, true);
  return ids;
};

const collectDescendantCategoryIds = (node: SpareCategoryTreeNode): number[] => {
  const ids = [node.id];
  for (const child of node.children ?? []) {
    ids.push(...collectDescendantCategoryIds(child));
  }
  return ids;
};

/** Direct item count for a category (not including descendants). */
export const countDirectItemsInCategory = (items: SpareItem[], categoryId: number): number =>
  items.reduce((n, item) => (item.categoryId === categoryId ? n + 1 : n), 0);

export const filterItemsForCategory = (
  items: SpareItem[],
  category: SpareCategoryTreeNode | null,
  includeSubcategories: boolean,
  searchTerm: string,
): SpareItem[] => {
  if (!category) return [];
  const allowed = new Set(
    includeSubcategories ? collectDescendantCategoryIds(category) : [category.id],
  );
  const q = searchTerm.trim().toLowerCase();
  return items.filter((item) => {
    if (item.categoryId == null || !allowed.has(item.categoryId)) return false;
    if (!q) return true;
    return (
      item.name.toLowerCase().includes(q) ||
      (item.vendorCode?.toLowerCase().includes(q) ?? false) ||
      (item.drawingNo?.toLowerCase().includes(q) ?? false) ||
      (item.serialNo?.toLowerCase().includes(q) ?? false) ||
      (item.notes?.toLowerCase().includes(q) ?? false)
    );
  });
};
